import * as React from 'react';
import styled from 'styled-components';
import { BREAKPOINTS, COLORS, ComponentTextStyle } from '../../styles/index';
import { Size } from '../../types';
import { ComponentMStyling, ComponentSStyling } from '../../styles';
import { useFocusVisibleRef } from '../../common';

export const Search = styled.input.attrs(() => ({ type: 'search' }))`
  appearance: none;
  width: calc(100% - 96px);

  /* To prevent browser putting its own cross inside the search bar */

  ::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  ::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  ::-webkit-search-decoration,
  ::-webkit-search-cancel-button,
  ::-webkit-search-results-button,
  ::-webkit-search-results-decoration {
    display: none;
  }

  ::placeholder {
    ${ComponentSStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
  }

  height: 32px;
  border-width: 0;
  margin-left: 48px;
  outline: none;

  ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.black)}
  ::placeholder {
    ${ComponentSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
  }

  ${BREAKPOINTS.MEDIUM} {
    ::placeholder {
      ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  &.small {
    margin-left: 44px;

    ::placeholder {
      ${ComponentSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  &.medium {
    margin-left: 48px;
    ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.black)}
    
    ::placeholder {
      ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  &:disabled {
    background: transparent;
    border: none;
  }
`;

type SearchBarInputProps = {
  id: string;
  searchTerm?: string;
  setSearchTerm: (term: string) => void;
  enterSearch: (e: any) => void;
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  size?: Size.Small | Size.Medium;
  onBlur?: (e: any) => void;
  focusParentRefs?: React.RefObject<any>[];
  onClick?: ()=>void;
};

const SearchBarInput = React.forwardRef<HTMLInputElement, SearchBarInputProps>((props: SearchBarInputProps, ref) => {
  const { id, searchTerm, setSearchTerm, enterSearch, placeholder, disabled, onKeyDown, size, onBlur, onClick } = props;

  const elementRef = useFocusVisibleRef(props.focusParentRefs || []);

  React.useImperativeHandle(ref, () => elementRef.current, [elementRef]);

  const isPressingEnter = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  };

  return (
    <Search
      placeholder={disabled ? '' : placeholder}
      ref={elementRef}
      id={id}
      data-testid={id}
      className={size ? size : ''}
      tabIndex={0}
      disabled={disabled}
      onChange={(e: any) => setSearchTerm(e.target.value)}
      value={searchTerm}
      onBlur={(e) => onBlur && onBlur(e)}
      onKeyDown={onKeyDown}
      onKeyPress={(e: any) => (isPressingEnter(e) ? enterSearch(e) : null)}
      onClick={onClick}
    />
  );
});

export default SearchBarInput;
